import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import {
    Lock, Info, FlaskConical, Atom, Zap, Target,
    BarChart3, Clock, ArrowRight, Download, ExternalLink,
    Gauge, Layers, Activity, Box, Scan
} from 'lucide-react';
import { SimulationProvider } from '../context/SimulationContext';
import Scene3D from '../components/simulation/Scene3D';
import { Link } from 'react-router-dom';
import { gsap, ScrollTrigger } from '../hooks/useGSAP';

// Use layout effect with SSR safety
const useIsomorphicLayoutEffect = typeof window !== 'undefined' ? useLayoutEffect : useEffect;

// Pre-computed results for the Z-SCANNER BL4S 2026 experiment
const proposalResults = {
    pvc: {
        name: 'PVC',
        formula: '(C₂H₃Cl)ₙ',
        X0: 15.8, // radiation length in cm
        density: 1.40, // g/cm³
        thetaRMS: 8.2, // mrad at 0.7 GeV/c, 2cm thickness
        variance: 67.2, // mrad²
        zEff: 12.4, // effective atomic number
    },
    delrin: {
        name: 'Delrin',
        formula: '(CH₂O)ₙ',
        X0: 35.0, // radiation length in cm
        density: 1.41, // g/cm³
        thetaRMS: 5.5, // mrad at 0.7 GeV/c, 2cm thickness
        variance: 30.3, // mrad²
        zEff: 7.0, // effective atomic number
    }
};

const ProposalContent = () => {
    const [activeMaterial, setActiveMaterial] = useState('pvc');
    const data = proposalResults[activeMaterial];

    // GSAP refs
    const headerRef = useRef(null);
    const sidebarRef = useRef(null);
    const thetaRef = useRef(null);

    // Animate header on mount
    useIsomorphicLayoutEffect(() => {
        if (!headerRef.current) return;

        const ctx = gsap.context(() => {
            gsap.fromTo(headerRef.current.children,
                { opacity: 0, y: 20 },
                { opacity: 1, y: 0, duration: 0.6, stagger: 0.1, ease: 'power4.out' }
            );
        }, headerRef);

        return () => ctx.revert();
    }, []);

    // Animate sidebar panels
    useIsomorphicLayoutEffect(() => {
        if (!sidebarRef.current) return;

        const ctx = gsap.context(() => {
            const panels = sidebarRef.current.querySelectorAll('.proposal-panel');
            gsap.fromTo(panels,
                { opacity: 0, x: 50 },
                { opacity: 1, x: 0, duration: 0.7, stagger: 0.12, ease: 'power4.out' }
            );
        }, sidebarRef);

        return () => ctx.revert();
    }, []);

    // Animate theta value change
    useEffect(() => {
        if (thetaRef.current) {
            gsap.fromTo(thetaRef.current,
                { scale: 1.2, opacity: 0.5 },
                { scale: 1, opacity: 1, duration: 0.4, ease: 'back.out(1.7)' }
            );
        }
    }, [activeMaterial]);

    return (
        <div className="h-[calc(100vh-64px)] flex flex-col overflow-hidden bg-slate-50">

            {/* Header Banner - Compact */}
            <div ref={headerRef} className="bg-[#f5f5f7] border-b border-gray-200 text-[#1d1d1f] shrink-0">
                <div className="container mx-auto px-6 py-4">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-3">
                                <FlaskConical size={28} className="text-[#0071e3]" />
                                Z-SCANNER: BL4S 2026 Proposal
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-gray-200 rounded text-xs font-normal ml-2 text-gray-600">
                                    <Lock size={10} />
                                    Locked
                                </span>
                            </h1>
                            <p className="text-gray-500 text-sm mt-1 max-w-xl">
                                Proton-based Multiple Coulomb Scattering tomography for material discrimination.
                            </p>
                        </div>
                        <div className="flex gap-2 shrink-0">
                            <button className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors text-sm font-medium">
                                <Download size={14} />
                                PDF
                            </button>
                            <a
                                href="https://simulator.virtualbeamline.org"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-3 py-2 bg-white text-[#0033A0] rounded-lg font-medium hover:bg-blue-50 transition-colors text-sm"
                            >
                                Build Your Own
                                <ArrowRight size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex flex-col lg:flex-row flex-1 overflow-hidden">

                {/* Left: 3D Visualization */}
                <div className="lg:flex-1 h-[400px] lg:h-full bg-slate-900 relative">
                    <Scene3D />

                    {/* Locked Indicator Overlay */}
                    <div className="absolute top-4 left-4 z-10">
                        <div className="bg-black/70 backdrop-blur-md text-white p-3 rounded-lg text-xs font-mono border border-white/10">
                            <div className="flex items-center gap-2 text-yellow-400 mb-2">
                                <Lock size={12} />
                                <span className="uppercase tracking-wider text-[10px]">Read-Only Mode</span>
                            </div>
                            <div className="text-slate-400">Target Material</div>
                            <div className="text-lg font-bold" style={{ color: activeMaterial === 'pvc' ? '#9B59B6' : '#1ABC9C' }}>
                                {data.name}
                            </div>
                        </div>
                    </div>

                    {/* Fixed Parameters Display */}
                    <div className="absolute bottom-4 left-4 z-10">
                        <div className="bg-black/80 backdrop-blur-md text-white p-4 rounded-xl text-xs font-mono border border-white/20 shadow-2xl min-w-[280px]">
                            <div className="text-[10px] uppercase tracking-wider text-yellow-400 mb-3 flex items-center gap-2">
                                <Lock size={10} />
                                Locked Experiment Parameters
                            </div>

                            <div className="space-y-2 mb-3 pb-3 border-b border-white/10">
                                <div className="flex justify-between">
                                    <span className="text-slate-400">Proton Momentum</span>
                                    <span className="text-cyan-400 font-bold">0.7 GeV/c</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-400">Phantom Thickness</span>
                                    <span className="text-indigo-400">2 cm</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-400">Facility</span>
                                    <span className="text-white">CERN T9 East Hall</span>
                                </div>
                            </div>

                            <div className="space-y-2">
                                <div className="flex justify-between">
                                    <span className="text-slate-400">X₀ (Radiation Length)</span>
                                    <span className="text-green-400 font-bold">{data.X0.toFixed(1)} cm</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-400">θ_RMS (Scattering)</span>
                                    <span className="text-yellow-400 font-bold">{data.thetaRMS.toFixed(1)} mrad</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-400">Z_eff</span>
                                    <span className="text-orange-400">{data.zEff}</span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-slate-400">Variance σ²</span>
                                    <span className={data.variance > 50 ? 'text-purple-400' : 'text-teal-400'}>
                                        {data.variance.toFixed(1)} mrad²
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right: Proposal Details */}
                <div ref={sidebarRef} className="lg:w-[450px] bg-white border-l border-slate-200 overflow-y-auto lg:h-full">
                    <div className="p-6 space-y-6">

                        {/* Material Selector (Visual Only) */}
                        <div className="proposal-panel">
                            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <Box size={14} />
                                Material Comparison
                            </h3>
                            <div className="grid grid-cols-2 gap-3">
                                {['pvc', 'delrin'].map(m => (
                                    <button
                                        key={m}
                                        onClick={() => setActiveMaterial(m)}
                                        className={`p-4 rounded-xl border-2 transition-all ${activeMaterial === m
                                            ? 'border-blue-500 bg-blue-50'
                                            : 'border-slate-200 hover:border-slate-300'
                                            }`}
                                    >
                                        <div className={`text-lg font-bold ${m === 'pvc' ? 'text-purple-600' : 'text-teal-600'}`}>
                                            {proposalResults[m].name}
                                        </div>
                                        <div className="text-xs text-slate-500">{proposalResults[m].formula}</div>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Experiment Configuration */}
                        <div className="proposal-panel bg-slate-50 rounded-xl p-4 border border-slate-100">
                            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <Target size={14} />
                                Experiment Configuration
                            </h3>
                            <div className="space-y-3">
                                {[
                                    { label: 'Proton Momentum', value: '0.7 GeV/c', icon: Zap },
                                    { label: 'Phantom Grid', value: '3�-3 tiles', icon: Layers },
                                    { label: 'Protons per Run', value: '10,000', icon: Activity },
                                    { label: 'Estimated Duration', value: '2 weeks', icon: Clock },
                                ].map((item, i) => (
                                    <div key={i} className="flex items-center justify-between">
                                        <div className="flex items-center gap-2 text-slate-600">
                                            <item.icon size={14} />
                                            {item.label}
                                        </div>
                                        <div className="font-mono font-bold text-slate-800">{item.value}</div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Detectors */}
                        <div className="proposal-panel">
                            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <Gauge size={14} />
                                Tracker Configuration
                            </h3>
                            <div className="space-y-2">
                                {[
                                    { name: 'Upstream Tracker 1', position: '-10 cm', status: 'active', color: 'bg-green-500' },
                                    { name: 'Upstream Tracker 2', position: '-5 cm', status: 'active', color: 'bg-green-500' },
                                    { name: 'Phantom Target', position: '0 cm', status: 'target', color: 'bg-purple-500' },
                                    { name: 'Downstream Tracker 1', position: '+5 cm', status: 'active', color: 'bg-blue-500' },
                                    { name: 'Downstream Tracker 2', position: '+10 cm', status: 'active', color: 'bg-blue-500' },
                                ].map((det, i) => (
                                    <div key={i} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg border border-slate-100">
                                        <div className="flex items-center gap-3">
                                            <div className={`w-3 h-3 rounded-full ${det.color}`} />
                                            <span className="font-medium text-slate-700">{det.name}</span>
                                        </div>
                                        <span className="text-xs font-mono text-slate-500">{det.position}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Expected Results */}
                        <div className="proposal-panel bg-gradient-to-br from-purple-50 to-teal-50 rounded-xl p-4 border border-purple-100">
                            <h3 className="text-sm font-bold text-purple-800 uppercase tracking-wider mb-3 flex items-center gap-2">
                                <Scan size={14} />
                                Expected Results
                            </h3>
                            <div className="space-y-3">
                                <div>
                                    <div className="text-xs text-purple-600 mb-1">Scattering Angle θ_RMS</div>
                                    <div className="flex items-end gap-2">
                                        <span ref={thetaRef} className="text-3xl font-bold text-purple-900">{data.thetaRMS.toFixed(1)} mrad</span>
                                        <span className="text-sm text-purple-600 mb-1">for {data.name}</span>
                                    </div>
                                </div>
                                <div className="text-sm text-purple-700">
                                    Radiation length X₀: <strong>{data.X0.toFixed(1)} cm</strong>
                                </div>
                                <div className="text-sm text-purple-700">
                                    Variance contrast: <strong>{((proposalResults.pvc.variance / proposalResults.delrin.variance) * 100).toFixed(0)}%</strong> (PVC/Delrin)
                                </div>
                                <div className="text-sm text-teal-700">
                                    Highland formula: θ_RMS = (13.6 MeV/βcp) �- √(L/X₀)
                                </div>
                            </div>
                        </div>

                        {/* Scientific Goal */}
                        <div className="border-t border-slate-200 pt-6">
                            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider mb-3">
                                Scientific Objective
                            </h3>
                            <p className="text-slate-600 text-sm leading-relaxed">
                                Our experiment aims to demonstrate <strong>proton-based MCS tomography</strong> for 
                                material discrimination. By measuring the RMS scattering angle of 0.7 GeV/c protons 
                                through PVC and Delrin phantoms, we can extract the radiation length X₀ and distinguish 
                                materials based on their <strong>Z² dependence</strong> of the scattering variance.
                                This technique has applications in medical imaging and homeland security.
                            </p>
                        </div>

                        {/* Links */}
                        <div className="flex gap-3">
                            <Link
                                to="/learn"
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium hover:bg-slate-200 transition-colors"
                            >
                                Learn More
                                <ExternalLink size={14} />
                            </Link>
                            <Link
                                to="/supplementary"
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-3 bg-[#0033A0] text-white rounded-xl font-medium hover:bg-blue-700 transition-colors"
                            >
                                View Data
                                <BarChart3 size={14} />
                            </Link>
                        </div>

                    </div>
                </div>
            </div>
        </div>
    );
};

// Main Proposal component with simulation context
const Proposal = () => {
    return (
        <SimulationProvider initialState={{
            particle: { name: 'Proton', symbol: 'p', mass: 0.938, massUnit: '938 MeV/c²', color: '#3498DB', lifetime: Infinity },
            beam: { momentum: 0.7, intensity: 10000 },
            beamline: { length: 0.2 },
            detectors: {
                upstreamTracker1: { enabled: true, position: -0.10 },
                upstreamTracker2: { enabled: true, position: -0.05 },
                phantom: { enabled: true, position: 0 },
                downstreamTracker1: { enabled: true, position: 0.05 },
                downstreamTracker2: { enabled: true, position: 0.10 },
            },
            physics: { magneticField: 0 },
        }}>
            <ProposalContent />
        </SimulationProvider>
    );
};

export default Proposal;
